import { ChangeDetectionStrategy, ChangeDetectorRef, Component, effect, inject, input, output } from "@angular/core";
import { FormBuilder, FormControl, FormGroup, ReactiveFormsModule } from "@angular/forms";
import { UiKitModule } from "@app/ui-kit.module";
import { PictureCardComponent } from "@components/picture-card/picture-card.component";
import { Gas, GasProduct, LocalizeGasNamePipe } from "@domain/gas/gas";
import { FgdsProductType, Product } from "@domain/product/product";
import { ProductImage } from "@domain/product/product-image";
import { ProductService } from "@domain/product/product.service";
import { first } from "rxjs";
import { TransmitterType } from "./transmitter-type";

@Component({
  selector: "app-transmitter-family-selector",
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: "./transmitter-family-selector.component.html",
  styleUrls: ["./transmitter-family-selector.component.scss"],
  imports: [UiKitModule, ReactiveFormsModule, LocalizeGasNamePipe, PictureCardComponent],
})
export class TransmitterFamilySelectorComponent {
  private formBuilder = inject(FormBuilder);
  private productService = inject(ProductService);
  private changeDetectorRef = inject(ChangeDetectorRef);

  readonly selectedSubstance = input<Gas | undefined>();
  readonly originalTransmitter = input<Product | undefined>();
  readonly originalTransmitterFamily = input<TransmitterType | undefined>();
  readonly familySelect$ = output<TransmitterType>();

  protected selectedGas?: Gas;
  protected selectedType?: TransmitterType;
  protected selectableFamilyTypes: TransmitterType[] = [];

  protected selectedFamilyForm: FormGroup<{
    selectedType: FormControl<TransmitterType | null>;
  }>;
  protected readonly ProductImage = ProductImage;

  private selectableGasProducts: GasProduct[] = [];
  private originalFamily?: TransmitterType;

  constructor() {
    this.selectedFamilyForm = this.formBuilder.group({
      selectedType: this.formBuilder.control<TransmitterType | null>(null),
    });

    this.selectedFamilyForm.valueChanges.subscribe(() => {
      const selectedType = this.selectedFamilyForm.controls.selectedType.value;
      this.selectedType = selectedType ?? undefined;
      if (selectedType) {
        this.familySelect$.emit(selectedType);
      }
    });

    effect(() => {
      const gas = this.selectedSubstance();
      this.clearFamilySelection();
      if (gas) {
        this.selectedGas = gas;
        this.collectProducts();
      }
    });

    effect(() => {
      this.originalFamily = this.originalTransmitterFamily();
      this.selectMatchingFamily();
    });

    effect(() => {
      this.originalTransmitter();
      this.selectMatchingFamily();
    });
  }

  protected select(type: TransmitterType) {
    this.selectedFamilyForm.patchValue({ selectedType: type });
  }

  private collectProducts() {
    if (!this.selectedGas) return;

    this.selectableGasProducts = this.selectedGas.products ?? [];
    const productIds = this.selectableGasProducts.map((gasProduct) => gasProduct.productId);
    if (!productIds.length) return;

    this.productService
      .getProductsByIds(productIds, FgdsProductType.TRANSMITTER)
      .pipe(first())
      .subscribe((products) => this.setSelectableFamilies(products));
  }

  private setSelectableFamilies(products: Product[]) {
    const productsById = new Map(products.map((product) => [product.id, product]));
    const availableFamilies = new Map<string, TransmitterType>();

    for (const gasProduct of this.selectableGasProducts) {
      const product = productsById.get(gasProduct.productId);
      if (!product?.isAvailable) continue;

      const familyNames = product.parentFamilies.length ? product.parentFamilies : [gasProduct.rawName];
      for (const familyName of familyNames) {
        if (!familyName) continue;
        const trimmedFamilyName = familyName.trim();
        const normalizedFamilyName = trimmedFamilyName.toLowerCase();
        if (!normalizedFamilyName) continue;

        const existingFamily = availableFamilies.get(normalizedFamilyName);
        if (existingFamily) {
          if (!existingFamily.cantoImageId && product.cantoImageId) {
            existingFamily.cantoImageId = product.cantoImageId;
          }
          continue;
        }

        availableFamilies.set(normalizedFamilyName, TransmitterType.fromFamilyName(trimmedFamilyName, product.cantoImageId));
      }
    }

    this.selectableFamilyTypes = [...availableFamilies.values()].sort((first, second) => first.name.localeCompare(second.name));
    this.selectMatchingFamily();
    this.changeDetectorRef.markForCheck();
  }

  private selectMatchingFamily() {
    if (!this.selectableFamilyTypes.length) return;

    const parentFamilyNames = this.originalTransmitter()?.parentFamilies ?? [];
    const matchingParentFamilies = this.selectableFamilyTypes.filter((family) =>
      parentFamilyNames.some((parentFamily) => parentFamily.toLowerCase() === family.name.toLowerCase()),
    );

    if (matchingParentFamilies.length === 1) {
      this.select(matchingParentFamilies[0]);
      return;
    }

    if (matchingParentFamilies.length > 1 && this.originalFamily) {
      const configuredFamily = matchingParentFamilies.find(
        (family) => family.name.toLowerCase() === this.originalFamily!.name.toLowerCase(),
      );
      if (configuredFamily) {
        this.select(configuredFamily);
      }
      return;
    }

    if (!this.originalFamily) return;

    const matchingFamily = this.selectableFamilyTypes.find(
      (family) => family.name.toLowerCase() === this.originalFamily!.name.toLowerCase(),
    );
    if (matchingFamily) {
      this.selectedFamilyForm.patchValue({ selectedType: matchingFamily });
    }
  }

  private clearFamilySelection() {
    this.selectedType = undefined;
    this.selectableFamilyTypes = [];
    this.selectedGas = undefined;
    this.selectableGasProducts = [];
    this.selectedFamilyForm.controls.selectedType.setValue(null, { emitEvent: false });
  }
}
